import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainBrandLogo, MyAccountIcon, ProfileIcon, SettingsIcon, LogoutIcon, SwitchCompanyIcon } from '../ui/Icons';
import { getMyCompanies, logout, switchMyCompany } from '../../services/api';
import { useDatabase } from '../../context/DatabaseContext';
import { useAuth } from '../../context/AuthContext';
import { LanguageSwitcher } from '../shared/LanguageSwitcher';

/**
 * Renders a company logo with a fallback:
 * 1. /logo_folder/<company.logo_path>
 * 2. Letter avatar
 */
const CompanyLogo = ({ name, logoPath, className = 'h-8 w-8', version = 1 }) => {
    const initialSrc = logoPath ? `/logo_folder/${encodeURIComponent(logoPath)}?v=${version}` : null;
    const [src, setSrc] = useState(initialSrc);
    const [failed, setFailed] = useState(!initialSrc);

    useEffect(() => {
        const nextSrc = logoPath ? `/logo_folder/${encodeURIComponent(logoPath)}?v=${version}` : null;
        setSrc(nextSrc);
        setFailed(!nextSrc);
    }, [logoPath, version]);

    if (failed) {
        return (
            <div className={`${className} rounded-lg bg-primary-100 border border-gray-200 flex items-center justify-center`}>
                <span className="text-primary-600 font-semibold text-sm">
                    {(name || '?').charAt(0).toUpperCase()}
                </span>
            </div>
        );
    }

    return (
        <img
            key={src}
            src={src}
            alt={name || 'Company'}
            onError={() => setFailed(true)}
            className={`${className} rounded-lg object-contain border border-gray-200 bg-white p-0.5`}
        />
    );
};

export const Header = () => {
    const { t } = useTranslation('layout');
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [activeCompanyId, setActiveCompanyId] = useState(null);
    const [isCompanyListOpen, setIsCompanyListOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { refreshDatabases } = useDatabase();
    const { user, isAdmin, setProfile, clear } = useAuth();

    // Load accessible companies for switcher
    useEffect(() => {
        const isAuthed =
            localStorage.getItem('isAuthenticated') === 'true' &&
            Boolean(localStorage.getItem('token'));
        if (!isAuthed) return;

        (async () => {
            try {
                const res = await getMyCompanies();
                const list = res.data?.data?.companies || [];
                const active = res.data?.data?.active_company_id ?? null;
                setCompanies(list);
                setActiveCompanyId(active);
            } catch {
                // Non-blocking: switcher simply won't render
            }
        })();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsProfileOpen(false);
                setIsCompanyListOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            // Ignore logout errors, proceed with local cleanup
        }
        
        // Clear local storage
        localStorage.removeItem('token');
        localStorage.removeItem('isAuthenticated');
        clear();

        // Navigate to login
        navigate('/login');
    };

    const handleSwitchCompany = async (nextCompanyId) => {
        const isAll = nextCompanyId === 'all';
        const parsed = isAll ? null : (nextCompanyId ? Number(nextCompanyId) : null);
        if (!isAll && (!parsed || parsed === activeCompanyId)) return;
        if (isAll && activeCompanyId == null) return;
        try {
            const res = await switchMyCompany(parsed);
            const profile = res.data?.data?.profile;
            if (profile) {
                setProfile(profile);
            }
            setActiveCompanyId(parsed);
            await refreshDatabases();
        } catch (e) {
            // If switch fails, revert UI selection
            setActiveCompanyId(activeCompanyId);
        }
    };

    const displayName = user?.name || user?.username || 'User';
    const displayEmail = user?.email || user?.company_name || '';
    const activeCompany = useMemo(() => {
        if (activeCompanyId == null) {
            return isAdmin ? { id: 'all', name: t('header.allCompanies') } : null;
        }
        return companies.find((c) => Number(c.id) === Number(activeCompanyId)) || null;
    }, [companies, activeCompanyId, isAdmin, t]);

    return (
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-30">
            {/* Left side: Logo */}
            <div className="flex items-center">
                <MainBrandLogo className="h-6 w-auto" />
            </div>

            {/* Center: Active company logo chip */}
            {activeCompany && activeCompany.id !== 'all' && (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-100 bg-gray-50">
                    <CompanyLogo name={activeCompany.name} logoPath={activeCompany.logo_path} className="h-6 w-6" />
                    <span className="text-xs font-semibold text-gray-700 max-w-[140px] truncate">
                        {activeCompany.name}
                    </span>
                </div>
            )}

            {/* Right side: Profile */}
            <div className="flex items-center space-x-4">
                <LanguageSwitcher />
                <div className="relative" ref={dropdownRef}>
                    <button 
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="flex items-center space-x-3 hover:bg-gray-50 p-1.5 rounded-xl transition-all outline-none"
                    >
                        <div className="relative">
                            <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center border border-gray-200">
                                <span className="text-primary-600 font-semibold text-sm">
                                    {displayName.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></span>
                        </div>
                        <div className="hidden md:block text-left">
                            <p className="text-sm font-semibold text-gray-900 leading-tight transition-all duration-300">
                                {displayName}
                            </p>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isProfileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Profile Dropdown */}
                    <div className={`absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 transform origin-top-right transition-all duration-300 ease-out ${
                        isProfileOpen 
                            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' 
                            : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                    }`}>
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center shadow-sm">
                                <span className="text-primary-600 font-bold text-lg">
                                    {displayName.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate">{displayName}</p>
                                <p className="text-xs text-gray-500 truncate transition-opacity duration-500">
                                    {displayEmail}
                                </p>
                            </div>
                        </div>

                        <div className="mt-2">
                            <DropdownItem icon={MyAccountIcon} label={t('header.myAccount')} />
                            <DropdownItem icon={ProfileIcon} label={t('header.profile')} />
                            <DropdownItem icon={SettingsIcon} label={t('header.settings')} onClick={() => navigate('/settings')} />
                            {companies.length > 1 || isAdmin ? (
                                <DropdownCompanySwitchAccordion
                                    icon={SwitchCompanyIcon}
                                    label={t('header.switchCompany')}
                                    activeCompany={activeCompany}
                                    companies={companies}
                                    activeCompanyId={activeCompanyId}
                                    isOpen={isCompanyListOpen}
                                    onToggle={() => setIsCompanyListOpen((v) => !v)}
                                    onSelect={async (companyId) => {
                                        await handleSwitchCompany(companyId);
                                        setIsCompanyListOpen(false);
                                    }}
                                />
                            ) : null}
                        </div>

                        <div className="mt-2 pt-2 border-t border-gray-50">
                            <DropdownItem
                                icon={LogoutIcon}
                                label={t('header.logout')}
                                onClick={handleLogout}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

const DropdownItem = ({ icon: Icon, label, rightIcon, onClick }) => (
    <button 
        onClick={onClick}
        className="w-full px-4 py-2.5 flex items-center justify-between text-gray-700 hover:bg-gray-50 transition-colors group"
    >
        <div className="flex items-center space-x-3">
            <Icon className="w-5 h-5 text-gray-600 group-hover:text-primary-600" />
            <span className="text-sm font-medium">{label}</span>
        </div>
        {rightIcon}
    </button>
);

const DropdownCompanySwitchAccordion = ({
    icon: Icon,
    label,
    activeCompany,
    companies,
    activeCompanyId,
    isOpen,
    onToggle,
    onSelect,
}) => (
    <div className="w-full">
        <button
            type="button"
            onClick={onToggle}
            className="w-full px-4 py-2.5 flex items-center justify-between text-gray-700 hover:bg-gray-50 transition-colors group"
        >
            <div className="flex items-center space-x-3 min-w-0">
                <Icon className="w-5 h-5 text-gray-600 group-hover:text-primary-600" />
                <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{label}</p>
                    {activeCompany?.name ? (
                        <p className="text-xs text-gray-500 truncate">{activeCompany.name}</p>
                    ) : null}
                </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <div className={`${isOpen ? 'block' : 'hidden'} px-2 pb-2`}>
            <div className="mt-1 rounded-xl border border-gray-100 bg-gray-50/50 overflow-hidden">
                {companies.map((c) => (
                    <CompanyRow
                        key={c.id}
                        company={c}
                        isActive={activeCompanyId == null ? c.id === 'all' : Number(c.id) === Number(activeCompanyId)}
                        onClick={() => onSelect(c.id)}
                    />
                ))}
            </div>
        </div>
    </div>
);

const CompanyRow = ({ company, isActive, onClick }) => {
    const { t } = useTranslation('layout');
    return (
    <button
        type="button"
        onClick={onClick}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-white transition-colors"
    >
        <div className="flex items-center gap-3 min-w-0">
            <CompanyLogo name={company?.name} logoPath={company?.logo_path} className="h-8 w-8 flex-shrink-0" />
            <div className="min-w-0 text-left">
                <p className="text-sm font-medium text-gray-900 truncate">{company?.name || t('header.company')}</p>
            </div>
        </div>
        {isActive ? (
            <span className="h-5 w-5 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
                <span className="text-green-600 text-xs font-bold">✓</span>
            </span>
        ) : (
            <span className="h-5 w-5" />
        )}
    </button>
    );
};